import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Button } from "src/components/ui/button";
import { QuestionType } from "src/lib/types";
import { generateId } from "src/lib/utils";
import QuestionCard from "./question-card";
import { useLanguage } from "../../context/LanguageContext";

export default function QuizBuilder({
  initialQuestions = [],
  onQuestionsChange,
  disabled = false,
  isSurvey = false,
}) {
  const { language } = useLanguage();
  const isInitialMount = useRef(true);

  const [questions, setQuestions] = useState(
    initialQuestions.length > 0
      ? initialQuestions
      : [
          {
            id: generateId(),
            type: isSurvey
              ? QuestionType.SINGLE_CHOICE
              : QuestionType.SURVEY_MULTIPLE_CHOICE,
            text: "",
            options: [
              {
                id: generateId(),
                text: "",
                ...(isSurvey ? {} : { isCorrect: false }),
              },
              {
                id: generateId(),
                text: "",
                ...(isSurvey ? {} : { isCorrect: false }),
              },
            ],
            imageUrl: null,
          },
        ]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (onQuestionsChange) onQuestionsChange(questions);
  }, [questions, onQuestionsChange]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const addQuestion = (type) => {
    const newQuestion = {
      id: generateId(),
      type,
      text: "",
      options: [],
      imageUrl: null,
    };
    if (isSurvey) {
      if (
        type === QuestionType.SINGLE_CHOICE ||
        type === QuestionType.SURVEY_MULTIPLE_CHOICE
      ) {
        newQuestion.options = [
          { id: generateId(), text: "" },
          { id: generateId(), text: "" },
        ];
      }
    } else {
      switch (type) {
        case QuestionType.MULTIPLE_CHOICE:
          newQuestion.options = [
            { id: generateId(), text: "", isCorrect: false },
            { id: generateId(), text: "", isCorrect: false },
          ];
          break;
        case QuestionType.TRUE_FALSE:
          newQuestion.options = [
            {
              id: generateId(),
              text: language === "ar" ? "صح" : "True",
              isCorrect: true,
            },
            {
              id: generateId(),
              text: language === "ar" ? "خطأ" : "False",
              isCorrect: false,
            },
          ];
          break;
        case QuestionType.PARAGRAPH:
        case QuestionType.FILE_UPLOAD:
          newQuestion.options = [];
          break;
      }
    }
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (updatedQuestion) =>
    setQuestions(
      questions.map((q) => (q.id === updatedQuestion.id ? updatedQuestion : q))
    );

  const removeQuestion = (id) =>
    setQuestions(questions.filter((q) => q.id !== id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setQuestions((items) => {
        const oldIndex = items.findIndex((q) => q.id === active.id);
        const newIndex = items.findIndex((q) => q.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-xl font-semibold">
          {isSurvey
            ? language === "ar"
              ? "أسئلة الاستطلاع"
              : "Survey Questions"
            : language === "ar"
            ? "أسئلة الاختبار"
            : "Quiz Questions"}
        </h2>
        <div className="flex gap-2 flex-wrap">
          {isSurvey ? (
            <>
              <Button
                onClick={() => addQuestion(QuestionType.SINGLE_CHOICE)}
                variant="outline"
                disabled={disabled}
                size="sm"
              >
                {language === "ar"
                  ? "إضافة سؤال اختيار واحد"
                  : "Add Single Choice"}
              </Button>
              <Button
                onClick={() => addQuestion(QuestionType.SURVEY_MULTIPLE_CHOICE)}
                variant="outline"
                disabled={disabled}
                size="sm"
              >
                {language === "ar"
                  ? "إضافة سؤال اختيار متعدد"
                  : "Add Multiple Choice"}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => addQuestion(QuestionType.MULTIPLE_CHOICE)}
                variant="outline"
                disabled={disabled}
                size="sm"
              >
                {language === "ar"
                  ? "إضافة سؤال اختيار من متعدد"
                  : "Add Multiple Choice"}
              </Button>
              <Button
                onClick={() => addQuestion(QuestionType.TRUE_FALSE)}
                variant="outline"
                disabled={disabled}
                size="sm"
              >
                {language === "ar" ? "إضافة سؤال صواب وخطأ" : "Add True/False"}
              </Button>
              <Button
                onClick={() => addQuestion(QuestionType.PARAGRAPH)}
                variant="outline"
                disabled={disabled}
                size="sm"
              >
                {language === "ar" ? "إضافة سؤال فقرة" : "Add Paragraph"}
              </Button>
            </>
          )}
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={questions.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          <AnimatePresence>
            {questions.map((question) => (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="mb-4"
              >
                <QuestionCard
                  question={question}
                  updateQuestion={updateQuestion}
                  removeQuestion={removeQuestion}
                  isEditMode={true}
                  disabled={disabled}
                  language={language}
                  isSurvey={isSurvey}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </SortableContext>
      </DndContext>
      {questions.length === 0 && (
        <div className="text-center p-8 border border-dashed rounded-md">
          <p className="text-muted-foreground">
            {language === "ar"
              ? "لا توجد أسئلة بعد. أضف سؤالاً باستخدام الأزرار أعلاه."
              : "No questions yet. Add a question using the buttons above."}
          </p>
        </div>
      )}
    </div>
  );
}
